import { Environment } from 'constants/'

/**
 * The fallback when no env file reached us — staging, and deliberately NOT testing.
 *
 * This branch is invisible: it fires on one missing variable and then loads a WHOLE environment
 * from inside the source, where no `.env.local` can override it and nothing on disk records that
 * it happened. Pointed at testing it was the quietest of the three ways a local run reached the
 * wrong tenant.
 *
 * ⚠️ Staging is the standing local target. Testing carries no data or bad data, so a run that
 * silently lands there renders a plausible screen about nobody — a worse failure than a crash.
 */
if (!process.env.REACT_APP_ACCOUNT_SERVICE_URL) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const dotenv = require('dotenv')

  dotenv.config({
    path: './environments/.env.staging',
  })
}

const ACCOUNT_SERVICE_URL = process.env.REACT_APP_ACCOUNT_SERVICE_URL as string
const ALLOWED_ORIGIN_CLIENT_URLS = process.env.REACT_APP_ALLOWED_ORIGIN_CLIENT_URLS as string
const APP_VERSION = process.env.REACT_APP_VERSION as string
const APPOINTMENT_SERVICE_URL_EU = process.env.REACT_APP_APPOINTMENT_SERVICE_URL_EU as string
const APPOINTMENT_SERVICE_URL_NA = process.env.REACT_APP_APPOINTMENT_SERVICE_URL_NA as string
const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE as string
const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID as string
const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN as string
const COCKPIT_URL = process.env.REACT_APP_COCKPIT_URL as string
const CUSTOMER_PROFILE_CLIENT_URL = process.env.REACT_APP_CUSTOMER_PROFILE_CLIENT_URL as string
const CUSTOMER_SERVICE_URL_EU = process.env.REACT_APP_CUSTOMER_SERVICE_URL_EU as string
const CUSTOMER_SERVICE_URL_NA = process.env.REACT_APP_CUSTOMER_SERVICE_URL_NA as string
const DEVICE_COUPLING_FORM_URL = process.env.REACT_APP_DEVICE_COUPLING_FORM_URL as string
const DEVICE_SERVICE_URL_EU = process.env.REACT_APP_DEVICE_SERVICE_URL_EU as string
const DEVICE_SERVICE_URL_NA = process.env.REACT_APP_DEVICE_SERVICE_URL_NA as string
const DOCUMENT_CLIENT_URL = process.env.REACT_APP_DOCUMENT_CLIENT_URL as string
const DOCUMENT_GENERATION_SERVICE_URL = process.env.REACT_APP_DOCUMENT_GENERATION_SERVICE_URL as string
const DOCUMENT_SERVICE_URL_EU = process.env.REACT_APP_DOCUMENT_SERVICE_URL_EU as string
const DOCUMENT_SERVICE_URL_NA = process.env.REACT_APP_DOCUMENT_SERVICE_URL_NA as string
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT as Environment
const FLOW_CLIENT_URL = process.env.REACT_APP_FLOW_CLIENT_URL as string
const FORCE_ENABLED_FEATURE_FLAGS = process.env.REACT_APP_FORCE_ENABLED_FEATURE_FLAGS?.split(',') ?? []
const HEARING_TEST_CLIENT_URL = process.env.REACT_APP_HEARING_TEST_CLIENT_URL as string
const HEARING_TEST_SERVICE_URL_NA = process.env.REACT_APP_HEARING_TEST_SERVICE_URL_NA as string
const ITEM_SERVICE_URL_NA = process.env.REACT_APP_ITEM_SERVICE_URL_NA as string
const LAUNCH_DARKLY_CLIENT_ID = process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID as string
const LAUNCH_DARKLY_TA_CLIENT_ID = process.env.REACT_APP_LAUNCH_DARKLY_TA_CLIENT_ID as string
const LIVECHAT_LICENSE = process.env.REACT_APP_LIVECHAT_LICENSE as string
const LOGPIT_URL = process.env.REACT_APP_LOGPIT_URL as string
const LOGROCKET_PROJECT_ID = process.env.REACT_APP_LOGROCKET_PROJECT_ID as string
const MIXPANEL_PROJECT_TOKEN = process.env.REACT_APP_MIXPANEL_PROJECT_TOKEN as string
const MRS_URL = process.env.REACT_APP_MRS_URL as string
const NOTE_SERVICE_URL_NA = process.env.REACT_APP_NOTE_SERVICE_URL_NA as string
const NOTIFICATION_SERVICE_URL = process.env.REACT_APP_NOTIFICATION_SERVICE_URL as string
const OPPORTUNITY_SERVICE_URL_EU = process.env.REACT_APP_OPPORTUNITY_SERVICE_URL_EU as string
const OPPORTUNITY_SERVICE_URL_NA = process.env.REACT_APP_OPPORTUNITY_SERVICE_URL_NA as string
const ORDER_SERVICE_URL_EU = process.env.REACT_APP_ORDER_SERVICE_URL_EU as string
const ORDER_SERVICE_URL_NA = process.env.REACT_APP_ORDER_SERVICE_URL_NA as string
const OTOSCOPY_SERVICE_URL = process.env.REACT_APP_OTOSCOPY_SERVICE_URL as string
const PARTNER_SERVICE_URL_EU = process.env.REACT_APP_PARTNER_SERVICE_URL_EU as string
const PARTNER_SERVICE_URL_NA = process.env.REACT_APP_PARTNER_SERVICE_URL_NA as string
const PARTNER_SUPPORT_PHONE_NUMBER = process.env.REACT_APP_PARTNER_SUPPORT_PHONE_NUMBER
const POST_APPOINTMENT_FORM_URL = process.env.REACT_APP_POST_APPOINTMENT_FORM_URL as string
const RFS_URL = process.env.REACT_APP_RFS_URL as string
const ROOM_SERVICE_URL = process.env.REACT_APP_ROOM_SERVICE_URL as string
const SALES_ORDER_FORM_URL = process.env.REACT_APP_SALES_ORDER_FORM_URL as string
const SALES_SERVICE_URL = process.env.REACT_APP_SALES_SERVICE_URL as string
const ORDER_FLOW_URL = process.env.REACT_APP_ORDER_FLOW_URL as string
const SALESFORCE_URL = process.env.REACT_APP_SALESFORCE_URL as string
const SCHEDULING_UI_URL = process.env.REACT_APP_SCHEDULING_UI_URL as string
const SHIPPING_SERVICE_URL_NA = process.env.REACT_APP_SHIPPING_SERVICE_URL_NA as string
const TYPEFORM_ORDER_FORM_URL = process.env.REACT_APP_TYPEFORM_ORDER_FORM_URL as string
const TYPEFORM_INSURANCE_URL = process.env.REACT_APP_TYPEFORM_INSURANCE_URL as string
const TYPEFORM_APPOINTMENT_URL = process.env.REACT_APP_TYPEFORM_APPOINTMENT_URL as string
const CHECKOUT_URL = process.env.REACT_APP_CHECKOUT_URL as string
const CORE_AUTH_SERVICE_CLIENT_ID = process.env.REACT_APP_CORE_AUTH_CLIENT_ID as string
const CORE_AUTH_SERVICE_CLIENT_SECRET = process.env.REACT_APP_CORE_AUTH_CLIENT_SECRET as string
const PRICE_LIST_URL = process.env.REACT_APP_PRICE_LIST_URL as string
const INVENTORY_CLIENT_URL = process.env.REACT_APP_INVENTORY_CLIENT_URL as string
const DEVICE_COUPLING_CLIENT_URL = process.env.REACT_APP_DEVICE_COUPLING_CLIENT_URL as string
const APPOINTMENT_FEEDBACK_URL = process.env.REACT_APP_APPOINTMENT_FEEDBACK_URL as string
const NEW_APPOINTMENT_FEEDBACK_URL = process.env.REACT_APP_NEW_APPOINTMENT_FEEDBACK_URL as string
const CHAT_CLIENT_URL = process.env.REACT_APP_CHAT_CLIENT_URL as string
const SALES_CLIENT_URL = process.env.REACT_APP_SALES_CLIENT_URL as string
const DOCUMENT_SIGNING_SERVICE_URL_EU = process.env.REACT_APP_DOCUMENT_SIGNING_SERVICE_URL_EU as string
const DOCUMENT_SIGNING_SERVICE_URL_NA = process.env.REACT_APP_DOCUMENT_SIGNING_SERVICE_URL_NA as string
const TA_LIGHT_UI_URL = process.env.REACT_APP_TA_LIGHT_UI_URL as string

/**
 * A LOCAL DEV SERVER MAY NOT RUN ON TESTING.
 *
 * Scoped to `NODE_ENV === 'development'`, which is `react-scripts start` and nothing else. The two
 * places testing is used ON PURPOSE both sit outside that: `npm run build:testing` builds with
 * `NODE_ENV=production` for the deployed testing site, and `npm run test:ci` runs the suite with
 * `NODE_ENV=test`. Neither trips this, and neither should.
 *
 * A throw rather than a warning because the failure it replaces is silent by nature — the app
 * boots, authenticates against the testing Auth0 tenant and draws a customer who is not the one
 * being looked for. Nothing on screen says which environment answered.
 */
if (process.env.NODE_ENV === 'development' && ENVIRONMENT === Environment.TESTING) {
  throw new Error(
    'REACT_APP_ENVIRONMENT=testing on a local dev server. This portal runs on STAGING locally — ' +
      'testing has no data or bad data. Put the staging values in .env.local (or ' +
      '.env.development.local, which outranks it); environments/.env.staging is the source.'
  )
}

const isDev = ENVIRONMENT === Environment.DEVELOPMENT
const liveEnvironments = [Environment.TESTING, Environment.PRODUCTION, Environment.STAGING]
const isLiveEnvironment = liveEnvironments.includes(ENVIRONMENT as Environment)

export {
  ACCOUNT_SERVICE_URL,
  ALLOWED_ORIGIN_CLIENT_URLS,
  APP_VERSION,
  APPOINTMENT_FEEDBACK_URL,
  APPOINTMENT_SERVICE_URL_EU,
  APPOINTMENT_SERVICE_URL_NA,
  AUTH0_AUDIENCE,
  AUTH0_CLIENT_ID,
  AUTH0_DOMAIN,
  CHAT_CLIENT_URL,
  CHECKOUT_URL,
  COCKPIT_URL,
  CORE_AUTH_SERVICE_CLIENT_ID,
  CORE_AUTH_SERVICE_CLIENT_SECRET,
  CUSTOMER_PROFILE_CLIENT_URL,
  CUSTOMER_SERVICE_URL_EU,
  CUSTOMER_SERVICE_URL_NA,
  DEVICE_COUPLING_CLIENT_URL,
  DEVICE_COUPLING_FORM_URL,
  DEVICE_SERVICE_URL_EU,
  DEVICE_SERVICE_URL_NA,
  DOCUMENT_CLIENT_URL,
  DOCUMENT_GENERATION_SERVICE_URL,
  DOCUMENT_SERVICE_URL_EU,
  DOCUMENT_SERVICE_URL_NA,
  DOCUMENT_SIGNING_SERVICE_URL_EU,
  DOCUMENT_SIGNING_SERVICE_URL_NA,
  ENVIRONMENT,
  FLOW_CLIENT_URL,
  FORCE_ENABLED_FEATURE_FLAGS,
  HEARING_TEST_CLIENT_URL,
  HEARING_TEST_SERVICE_URL_NA,
  INVENTORY_CLIENT_URL,
  isDev,
  isLiveEnvironment,
  ITEM_SERVICE_URL_NA,
  LAUNCH_DARKLY_CLIENT_ID,
  LAUNCH_DARKLY_TA_CLIENT_ID,
  LIVECHAT_LICENSE,
  LOGPIT_URL,
  LOGROCKET_PROJECT_ID,
  MIXPANEL_PROJECT_TOKEN,
  MRS_URL,
  NEW_APPOINTMENT_FEEDBACK_URL,
  NOTE_SERVICE_URL_NA,
  NOTIFICATION_SERVICE_URL,
  OPPORTUNITY_SERVICE_URL_EU,
  OPPORTUNITY_SERVICE_URL_NA,
  ORDER_FLOW_URL,
  ORDER_SERVICE_URL_EU,
  ORDER_SERVICE_URL_NA,
  OTOSCOPY_SERVICE_URL as OTOSCOPY_SERVICE_URL_EU,
  OTOSCOPY_SERVICE_URL as OTOSCOPY_SERVICE_URL_NA,
  PARTNER_SERVICE_URL_EU,
  PARTNER_SERVICE_URL_NA,
  PARTNER_SUPPORT_PHONE_NUMBER,
  POST_APPOINTMENT_FORM_URL,
  PRICE_LIST_URL,
  RFS_URL,
  ROOM_SERVICE_URL,
  SALES_CLIENT_URL,
  SALES_ORDER_FORM_URL,
  SALES_SERVICE_URL,
  SALESFORCE_URL,
  SCHEDULING_UI_URL,
  SHIPPING_SERVICE_URL_NA,
  TA_LIGHT_UI_URL,
  TYPEFORM_APPOINTMENT_URL,
  TYPEFORM_INSURANCE_URL,
  TYPEFORM_ORDER_FORM_URL,
}
